import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import c from 'classnames';

import {
  sendBirdSelectors,
  useSendbirdStateContext
} from '@sendbird/uikit-react';
import { UserEventHandler } from '@sendbird/chat';

import Navbar from '../Navbar';
import NavbarMobile from '../NavbarMobiles';
import Menu from '../Menu';
import MenuMobile from '../MenuMobile';
import Container from '../Container';
// import AppLoader from '../AppLoader';
import DownScreenMessage from '../DownScreenMessage';
import CriticalModal from './components/CriticalModal';
import OfflineModal from './components/OfflineModal';
import BatchMessageComposer from '../BatchMessage/BatchMessageComposer';
import BatchMessageReport from '../BatchMessage/BatchMessageReport';
import BatchMessageStatus from '../BatchMessage/BatchMessageStatus';
import { setSendbirdUnreadMessages } from '../../sagas/sendbird/unreadMessages/actions';
import AIAssistant from '../AIAssistant';
import AIAssistantLauncher from '../AIAssistant/components/AIAssistantLauncher';
import AIAssistantBar from '../AIAssistant/components/AIAssistantBar';
import PricingModalGlobal from '../PricingModalGlobal';

import './_layout.scss';

const SendbirdUnreadCounter = () => {
  const dispatch = useDispatch();
  const context = useSendbirdStateContext();
  const initializedSdkRef = useRef(null);

  useEffect(() => {
    const sdk = sendBirdSelectors.getSdk(context);

    if (!sdk?.groupChannel || initializedSdkRef.current === sdk) return;

    let isMounted = true;
    initializedSdkRef.current = sdk;

    const fetchUnreadCount = async () => {
      try {
        const count = await sdk.groupChannel.getTotalUnreadMessageCount();

        if (isMounted) {
          dispatch(setSendbirdUnreadMessages(count || 0));
        }
      } catch (error) {
        console.error('Failed to fetch Sendbird unread count', error);
      }
    };

    fetchUnreadCount();

    const handler = new UserEventHandler({
      onTotalUnreadMessageCountChanged: unreadMessageCount => {
        const count =
          unreadMessageCount?.groupChannelCount ??
          unreadMessageCount?.feedChannelCount ??
          unreadMessageCount ??
          0;

        dispatch(setSendbirdUnreadMessages(count));
      }
    });

    sdk.addUserEventHandler('layout-unread-messages-count', handler);

    return () => {
      isMounted = false;
      sdk.removeUserEventHandler('layout-unread-messages-count');
    };
  }, [context]);

  return null;
};

const Layout = ({ sendbirdReady = false, children }) => {
  const [openDrawer, toggleMobileDrawer] = useState(false);
  const { navbar, menu, whiteBackground, message } = useSelector(s => s.layout);
  const { open: aiAssistantOpen, mode: aiAssistantMode } = useSelector(state => state.aiAssistant || { open: false, mode: 'fullscreen' });
  const [offline, updateOffline] = useState(false);

  useEffect(() => {
    !window.navigator.onLine && updateOffline(true);

    window.addEventListener('offline', offLine);
    window.addEventListener('online', onLine);

    return () => {
      window.removeEventListener('offline', offLine);
      window.removeEventListener('online', onLine);
    };
  }, []); // eslint-disable-line

  const onLine = () => updateOffline(false);
  const offLine = () => updateOffline(true);

  const toggleDrawer = () => event => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;

    toggleMobileDrawer(!openDrawer);
  };

  const containerClass = c({ 'navbar-non': !navbar });
  const contentClass = c('content', {
    'white': whiteBackground,
    'no-menu': !menu,
    'aiAssistant-open': aiAssistantOpen && aiAssistantMode === 'sidebar'
  });

  return (
    <div className="layout">
      {sendbirdReady && (
        <SendbirdUnreadCounter />
      )}

      {/*<AppLoader />*/}

      <MediaQuery minWidth={993}>
        {navbar && <Navbar />}
      </MediaQuery>

      <MediaQuery maxWidth={992}>
        <NavbarMobile
          open={openDrawer}
          toggleDrawer={() => toggleDrawer()}
        />
      </MediaQuery>

      <div className="app-container">
        {menu && <Menu />}

        <MenuMobile
          open={openDrawer}
          toggleDrawer={() => toggleDrawer()}
        />

        <div className={contentClass}>
          <Container containerStyle={containerClass}>
            {children}
          </Container>
        </div>
        <AIAssistant allowDevAuth={false} />
      </div>

      <BatchMessageComposer />
      <BatchMessageReport />
      <BatchMessageStatus />
      <CriticalModal />
      <OfflineModal open={offline} />
      <DownScreenMessage message={message} open={Boolean(message)} />
      <PricingModalGlobal />
      <AIAssistantLauncher />
      <AIAssistantBar />
    </div>
  );
};

Layout.propTypes = {
  sendbirdReady: PropTypes.bool,
  children: PropTypes.node.isRequired
};

export default Layout;
